/**
 * Recados entre el marco y una pantalla.
 *
 * Es el mismo mecanismo que `scan-request.ts` —y por la misma razon— pero para
 * los avisos de la campanita: tocar un aviso tiene que ABRIR ALGO, y ese algo
 * vive en la pantalla de destino, no en el marco.
 *
 *   - "Reporte del jueves 13" abre el visor del corte, que lo arma `CutReport`
 *     con los mismos criterios que el boton de siempre en Caja.
 *   - "Se acabo Cebolla blanca" deja la palabra en el buscador del inventario,
 *     de modo que se cae justo en ese renglon, con su boton de resurtir.
 *
 * Duplicar esas dos cosas en el marco seria tener dos versiones de la misma
 * regla esperando a separarse. Asi que el marco no abre nada: NAVEGA Y DEJA EL
 * RECADO, y la pantalla lo atiende cuando se monta.
 *
 * El caso interesante es justamente ese cruce:
 *
 *   1. La dueña esta en la caja y toca el aviso del reporte.
 *   2. El marco navega a Caja y deja el recado. Todavia no hay quien escuche:
 *      la pantalla se pide con `lazy` y aun no ha llegado.
 *   3. La pantalla se monta, se suscribe, encuentra el recado y abre el visor.
 *
 * Si ya estaba en esa pantalla, el paso 2 encuentra al suscriptor y dispara de
 * inmediato —`navigate` a la direccion actual no hace nada, asi que el recado es
 * lo unico que ocurre—.
 *
 * Un recado sin destinatario se guarda, pero no puede quedarse ahi para siempre:
 * la navegacion ocurre en el mismo toque, asi que lo unico que separa el recado
 * de su lector es lo que tarda en bajar el trozo de la pantalla. Solo puede
 * haber UNO pendiente, y el que llega pisa al anterior: tocar dos avisos
 * seguidos es abrir el segundo.
 */

import { useEffect, useRef } from 'react';

export type ScreenIntent =
  /** Caja: abrir el visor del reporte del dia. */
  | { kind: 'daily-report' }
  /** Inventario: dejar este nombre en el buscador. */
  | { kind: 'find-product'; name: string };

type IntentKind = ScreenIntent['kind'];
type Listener = (intent: ScreenIntent) => void;

const listeners = new Map<IntentKind, Set<Listener>>();

let pending: ScreenIntent | null = null;

/** Pide que una pantalla haga algo al llegar. La llama el marco. */
export function requestScreenIntent(intent: ScreenIntent): void {
  const set = listeners.get(intent.kind);
  if (set !== undefined && set.size > 0) {
    for (const listener of set) listener(intent);
    return;
  }
  pending = intent;
}

/** Atiende los recados de un tipo. La usa la pantalla de destino. */
export function useScreenIntent<K extends IntentKind>(
  kind: K,
  onIntent: (intent: Extract<ScreenIntent, { kind: K }>) => void,
): void {
  // La funcion casi siempre es nueva en cada render del padre; guardarla en una
  // referencia deja al efecto dependiendo solo del tipo, que no cambia.
  const onIntentRef = useRef(onIntent);
  onIntentRef.current = onIntent;

  useEffect(() => {
    const listener: Listener = (intent) => {
      onIntentRef.current(intent as Extract<ScreenIntent, { kind: K }>);
    };

    let set = listeners.get(kind);
    if (set === undefined) {
      set = new Set<Listener>();
      listeners.set(kind, set);
    }
    set.add(listener);

    // El recado que llego antes de que existiera esta pantalla.
    if (pending !== null && pending.kind === kind) {
      const intent = pending;
      pending = null;
      listener(intent);
    }

    const mounted = set;
    return () => {
      mounted.delete(listener);
    };
  }, [kind]);
}
